import type {
  TemplateDocumentSettings,
  TemplateSummary,
  TemplateUsageSettings,
  UploadedPdfFile,
} from './template.models';
import type { UserPreferences } from './auth.models';
import type { BrandSignatureFrameSettings } from './brand.models';

export const DOCUMENT_STATUSES = [
  'draft',
  'scheduled',
  'waiting_for_others',
  'completed',
  'declined',
  'revoked',
  'expired',
  'needs_attention',
] as const;

export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const EDITABLE_DOCUMENT_STATUSES: DocumentStatus[] = [
  'draft',
  'scheduled',
  'needs_attention',
  'waiting_for_others',
];

export interface DocumentRecipient {
  id: string;
  roleId: string;
  roleName: string;
  name: string;
  email: string;
  order: number;
  status:
    | 'pending'
    | 'invited'
    | 'viewed'
    | 'completed'
    | 'declined'
    | 'expired'
    | 'delivery_failed'
    | 'revoked';
  invitedAt?: string;
  viewedAt?: string;
  completedAt?: string;
  declinedAt?: string;
  declineReason?: string;
  deliveryError?: string;
}

export interface DocumentSubmission {
  recipientId: string;
  submittedAt: string;
  values: Array<{
    fieldId: string;
    value: unknown;
  }>;
}

export interface DocumentTimelineEvent {
  at: string;
  actorType: 'system' | 'sender' | 'recipient';
  actorId?: string;
  event: string;
  message: string;
  details?: unknown;
}

export interface DocumentInfo {
  title: string;
  message?: string;
}

export interface DocumentFieldDraft {
  id: string;
  fieldKey?: string;
  type:
    | 'signature'
    | 'initials'
    | 'textbox'
    | 'date_signed'
    | 'checkbox'
    | 'radio'
    | 'name'
    | 'email'
    | 'title'
    | 'company'
    | 'editable_date'
    | 'image'
    | 'dropdown';
  role: string;
  page: number;
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  required: boolean;
  label?: string;
  placeholder?: string;
  settings?: unknown;
}

export interface DocumentPayload {
  sourceType: 'upload' | 'template';
  templateId?: string;
  sourceFile: UploadedPdfFile;
  fields: DocumentFieldDraft[];
  signingOrderEnabled: boolean;
  recipients: Array<{
    id: string;
    roleId: string;
    roleName: string;
    name: string;
    email: string;
    order: number;
  }>;
  ccEmails: string[];
  document: DocumentInfo;
  tags: string[];
  documentSettings: TemplateDocumentSettings;
  expiryAt?: string;
}

export interface DocumentSummary extends DocumentPayload {
  _id: string;
  ownerUserId?: string;
  sourceTemplateId?: string;
  sourceTemplateUsageSettings?: TemplateUsageSettings;
  status: DocumentStatus;
  statusReason?: string;
  scheduledFor?: string;
  sentAt?: string;
  revokedAt?: string;
  completedAt?: string;
  expiryAt: string;
  recipients: DocumentRecipient[];
  submissions?: DocumentSubmission[];
  timeline?: DocumentTimelineEvent[];
  createdAt: string;
  updatedAt: string;
  sourceTemplateName?: string;
  finalSignedFile?: {
    fileId: string;
    s3Key: string;
    bucket: string;
    originalName: string;
    sizeBytes: number;
    pageCount: number;
    generatedAt: string;
  };
  signatureCertificateFile?: {
    fileId: string;
    s3Key: string;
    bucket: string;
    originalName: string;
    sizeBytes: number;
    pageCount: number;
    generatedAt: string;
  };
}

export interface DocumentListResponse {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  items: DocumentSummary[];
}

export interface DashboardDocumentListItem {
  id: string;
  title: string;
  status: DocumentStatus;
  statusReason?: string;
  counterpartyName?: string;
  counterpartyEmail?: string;
  updatedAt: string;
  completedAt?: string;
  scheduledFor?: string;
}

export interface DashboardDocumentSection {
  count: number;
  items: DashboardDocumentListItem[];
}

export interface DashboardDocumentMetric {
  total: number;
  sent: number;
  received: number;
}

export interface DashboardSummaryResponse {
  waitingForMe: DashboardDocumentSection;
  waitingForOthers: DashboardDocumentSection;
  needsAttention: DashboardDocumentSection;
  recentlyCompleted: DashboardDocumentSection;
  metrics: {
    totalDocuments: DashboardDocumentMetric;
    thisMonthDocuments: DashboardDocumentMetric;
  };
}

export interface SendDocumentResponse {
  status: 'queued' | 'scheduled';
  document: DocumentSummary;
}

export interface SendReminderResponse {
  document: DocumentSummary | null;
  queuedRecipientIds: string[];
}

export interface SignSessionResponse {
  viewMode: 'active' | 'completed' | 'declined' | 'expired' | 'deleted';
  preferences: Pick<UserPreferences, 'dateFormat' | 'timeFormat' | 'timeZone'>;
  document: {
    id: string;
    title: string;
    message?: string;
    status: DocumentStatus;
    expiryAt: string;
    sourceFile: {
      fileId: string;
      originalName: string;
      pageCount: number;
    };
  };
  recipient: {
    id: string;
    roleId: string;
    roleName: string;
    name: string;
    email: string;
    order: number;
    status: string;
    completedAt?: string;
    declinedAt?: string;
  };
  fields: DocumentFieldDraft[];
  values: Array<{ fieldId: string; value: unknown }>;
  pdfUrl: string;
  brandSignatureFrame?: BrandSignatureFrameSettings;
}

export interface PublicReviewSessionResponse {
  document: {
    id: string;
    title: string;
    message?: string;
    status: DocumentStatus;
    expiryAt: string;
    sentAt?: string;
    completedAt?: string;
    sourceFile: {
      fileId: string;
      originalName: string;
      pageCount: number;
    };
  };
  recipients: Array<{
    id: string;
    roleId: string;
    roleName: string;
    name: string;
    order: number;
    status: string;
    completedAt?: string;
    declinedAt?: string;
  }>;
  fields: DocumentFieldDraft[];
  values: Array<{ fieldId: string; value: unknown }>;
  pdfUrl: string;
  brandSignatureFrame?: BrandSignatureFrameSettings;
}

export interface PublicDocumentConfigSessionResponse {
  document: {
    id: string;
    title: string;
    message?: string;
    status: DocumentStatus;
    canEditFields: boolean;
    sourceType: 'upload' | 'template';
    sourceFile: {
      fileId: string;
      originalName: string;
      pageCount: number;
    };
    ccEmails: string[];
    tags: string[];
    documentSettings: TemplateDocumentSettings;
  };
  recipients: Array<{
    id: string;
    roleId: string;
    roleName: string;
    name: string;
    email: string;
    order: number;
    status: string;
  }>;
  fields: DocumentFieldDraft[];
  pdfUrl: string;
}

export interface PublicDocumentConfigSaveResponse {
  documentId: string;
  status: DocumentStatus;
  fields: DocumentFieldDraft[];
  updatedAt: string;
}

export interface PublicDocumentConfigUrlResponse {
  documentId: string;
  status: DocumentStatus;
  canEditFields: boolean;
  configureFieldsUrl: string;
}

export interface PublicDocumentConfigSendResponse {
  status: 'queued' | 'scheduled';
  document: DocumentSummary;
}

export interface TemplateChoiceSummary {
  id: string;
  name: string;
  description?: string;
  template: TemplateSummary;
}
